/**
 * Accept Risk Modal Component
 * 
 * Modal for accepting a violation risk with required reason.
 */

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Violation } from '@/hooks/useCompileGateViewModel';

interface AcceptRiskModalProps {
  violation: Violation;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const AcceptRiskModal: React.FC<AcceptRiskModalProps> = ({
  violation,
  onConfirm,
  onCancel
}) => {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#050B17] border border-[#111827] rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            <h2 className="text-lg font-semibold">Accept Risk</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
          <div className="text-sm font-medium text-yellow-400 mb-1">Violation</div>
          <div className="text-sm text-gray-300">{violation.rule_name}</div>
          {violation.activity_id && (
            <div className="text-xs text-gray-400 mt-1">Activity: {violation.activity_id}</div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason for Accepting Risk <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this risk is acceptable..."
              className="w-full px-3 py-2 bg-[#030712] border border-[#111827] rounded text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500"
              rows={4}
              required
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!reason.trim()}>
              Accept Risk
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
